import "../styles/tokens.css";
import "../styles/base.css";
import "../styles/components.css";
import "../styles/responsive.css";

import { localePath, messages } from "../i18n";
import { buildSiteFooter } from "./footer";
import { buildArticleLocaleControl, buildSiteHeader } from "./header";
import type { Locale } from "../i18n/types";
import type { ArticlePageMetadata } from "../shared/articles";

const root = document.querySelector<HTMLElement>("#app");
if (!root) throw new Error("Missing #app root");

const state = {
  locale: (document.documentElement.lang === "en" ? "en" : "zh-TW") as Locale
};

function el<T extends HTMLElement>(tag: string, className?: string, text?: string): T {
  const element = document.createElement(tag) as T;
  if (className) element.className = className;
  if (text) element.textContent = text;
  return element;
}

function buildHeader(copy: typeof messages["zh-TW"], article: Partial<ArticlePageMetadata>) {
  const languageControl = buildArticleLocaleControl(copy, state.locale, article.translationUrl);

  return buildSiteHeader(
    copy,
    state.locale,
    languageControl,
    {
      articlesHref: state.locale === "en" ? "/en/articles/" : "/articles/",
      articlesLabel: state.locale === "en" ? "IP Knowledge" : "科普文章",
      esimLabel: state.locale === "en" ? "Caylet eSIM" : "自研 eSIM"
    }
  );
}

// footer logic replaced by buildSiteFooter

function renderArticle() {
  const copy = messages[state.locale];

  let article: Partial<ArticlePageMetadata> = {};
  try {
    const dataEl = document.getElementById("article-data");
    if (dataEl && dataEl.textContent) {
      article = JSON.parse(dataEl.textContent);
    }
  } catch {
    // Ignore parse error
  }

  const frag = document.createDocumentFragment();
  frag.append(buildHeader(copy, article));

  const main = el("main");
  main.id = "main-content";

  const container = el("article", "article-page-container article-page-panel");

  // Breadcrumb
  const breadcrumb = el("nav", "breadcrumb article-breadcrumb");
  breadcrumb.setAttribute("aria-label", "Breadcrumb");
  const homeHref = state.locale === "en" ? "/en/" : "/";
  const listHref = state.locale === "en" ? "/en/articles/" : "/articles/";
  const listLabel = state.locale === "en" ? "IP Knowledge" : copy.articles?.title || "科普文章";
  const homeLink = el<HTMLAnchorElement>("a", "breadcrumb-link", "Home");
  homeLink.href = homeHref;
  const listLink = el<HTMLAnchorElement>("a", "breadcrumb-link", listLabel);
  listLink.href = listHref;

  const sep1 = el("span", "breadcrumb-separator", "/");
  const sep2 = el("span", "breadcrumb-separator", "/");
  const categorySpan = el("span", "breadcrumb-current", article.category || "");

  breadcrumb.append(homeLink, sep1, listLink, sep2, categorySpan);
  container.append(breadcrumb);

  // Article Header
  const header = el("header", "article-page-header");

  if (article.category) {
    const categoryBadge = el("span", "article-category-badge", article.category);
    header.append(categoryBadge);
  }

  // Title
  const title = el("h1", "article-page-title", article.title || "");
  header.append(title);

  // Meta info bar (Author, Date, Reading Time)
  const meta = el("div", "article-meta-row");
  const dateStr = article.publishedAt
    ? state.locale === "en"
      ? new Date(article.publishedAt).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC"
        })
      : new Date(article.publishedAt).toLocaleDateString("zh-TW", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          timeZone: "UTC"
        })
    : "";

  meta.innerHTML = `
    <div class="article-meta-item">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
      <span>${article.author || "Aiden"}</span>
    </div>
    <div class="article-meta-item">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
        <line x1="16" y1="2" x2="16" y2="6"></line>
        <line x1="8" y1="2" x2="8" y2="6"></line>
        <line x1="3" y1="10" x2="21" y2="10"></line>
      </svg>
      <span>${copy.articles?.published || "發布於"}: ${dateStr}</span>
    </div>
    <div class="article-meta-item">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <polyline points="12 6 12 12 16 14"></polyline>
      </svg>
      <span>${state.locale === "en" ? "5 min read" : "約 5 分鐘閱讀"}</span>
    </div>
  `;

  header.append(meta);
  container.append(header);

  // Executive Summary Card (Replaces the old yellow risk-warning box)
  if (article.summary) {
    const summaryCard = el("div", "article-executive-summary article-summary");
    const summaryHeader = el("div", "article-summary-header");
    summaryHeader.innerHTML = `
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
      </svg>
      <span>${state.locale === "en" ? "Executive Summary" : "核心摘要與觀點"}</span>
    `;
    const summaryText = el("p", "article-summary-text", article.summary);
    summaryCard.append(summaryHeader, summaryText);
    container.append(summaryCard);
  }

  // Content HTML
  const contentNode = el("div", "article-body-content");

  // Get raw HTML from the DOM (injected by SSG)
  const rawHtml = document.getElementById("article-body");
  if (rawHtml) {
    contentNode.innerHTML = rawHtml.innerHTML;
    rawHtml.remove();
  }
  container.append(contentNode);

  // Bottom Interactive Banner & Back Link
  const ctaWrap = el("div", "article-footer-cta article-cta");

  const ctaCard = el("div", "article-cta-card");
  ctaCard.innerHTML = `
    <div class="article-cta-content">
      <div class="article-cta-badge">${state.locale === "en" ? "Live IP Diagnostics" : "即時公網環境檢測"}</div>
      <h3 class="article-cta-title">${state.locale === "en" ? "Analyze Your Network & IP Signals" : "一鍵檢測您目前的網路與 IP 風險"}</h3>
      <p class="article-cta-desc">${copy.evidence.disclaimer}</p>
    </div>
  `;

  const ctaBtn = el<HTMLAnchorElement>(
    "a",
    "primary-button article-cta-button",
    state.locale === "en" ? "Run Full IP Check →" : "前往 IP 完整檢測 →"
  );
  ctaBtn.href = localePath(state.locale);
  ctaCard.append(ctaBtn);

  const backLink = el<HTMLAnchorElement>(
    "a",
    "article-back-link",
    state.locale === "en" ? "← Back to IP Knowledge Library" : "← 返回科普文章目錄"
  );
  backLink.href = listHref;

  ctaWrap.append(ctaCard, backLink);
  container.append(ctaWrap);
  main.append(container);

  const { footer, modals } = buildSiteFooter(copy);
  frag.append(main);
  frag.append(footer, ...modals);

  root!.innerHTML = "";
  root!.append(frag);
}

renderArticle();
